import { useState, useEffect, useCallback } from 'react'
import { generateScreenshotFilename } from '../utils/formatters'

/**
 * CLIPBOARD PASTE HOOK
 *
 * Listens for paste events on the document and extracts image files
 * from the clipboard. Only active when isActive is true and not disabled.
 * Skips paste events targeting text inputs to avoid interfering with
 * normal text paste behavior.
 *
 * @param {Object} options
 * @param {function} options.onPaste - Callback receiving array of File objects
 * @param {boolean} options.isActive - Whether paste listening is enabled
 * @param {boolean} options.disabled - Whether the upload target is disabled
 * @returns {{ isPasting: boolean }}
 */
export function useClipboardPaste({ onPaste, isActive, disabled }) {
  const [isPasting, setIsPasting] = useState(false)

  const handlePaste = useCallback((e) => {
    if (!isActive || disabled) return

    // Don't intercept paste in text input elements
    const target = e.target
    if (
      target.tagName === 'INPUT' ||
      target.tagName === 'TEXTAREA' ||
      target.isContentEditable
    ) {
      return
    }

    const items = e.clipboardData?.items
    if (!items) return

    // Find the first image item in clipboard
    for (const item of items) {
      if (item.type.startsWith('image/')) {
        e.preventDefault()

        const blob = item.getAsFile()
        if (!blob) return

        // Create a new File with a meaningful name
        const fileName = generateScreenshotFilename(item.type)
        const file = new File([blob], fileName, { type: item.type })

        setIsPasting(true)
        // Call onPaste with the file wrapped in array for handleFiles compatibility
        Promise.resolve(onPaste([file])).finally(() => {
          setIsPasting(false)
        })

        return // Only process first image
      }
    }
  }, [isActive, disabled, onPaste])

  useEffect(() => {
    if (!isActive || disabled) return

    document.addEventListener('paste', handlePaste)
    return () => {
      document.removeEventListener('paste', handlePaste)
    }
  }, [isActive, disabled, handlePaste])

  return { isPasting }
}
